/**
 * Organisations-Umschalter in der Kopfleiste (Mandant, A2).
 *
 * Erscheint nur, wenn der Benutzer mehr als eine Organisation sieht
 * (mehrere `kunde:*`-Gruppen oder Reviewer). Die Wahl wird als
 * `X-Organisation`-Header mitgeschickt; alle Listen laden danach neu.
 * „Alle" = keine Einengung.
 */
import { useEffect } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Building2 } from "lucide-react"
import { api } from "@/services/api"
import { useOrganisationStore } from "@/store/organisation"

export function OrganisationsUmschalter() {
  const gewaehlt = useOrganisationStore((s) => s.gewaehlt)
  const setGewaehlt = useOrganisationStore((s) => s.setGewaehlt)
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ["organisationen"],
    queryFn: api.organisationen.list,
    staleTime: 5 * 60 * 1000,
  })

  // Gespeicherte Wahl, die es nicht (mehr) gibt → zurücksetzen
  useEffect(() => {
    if (data && gewaehlt && !data.some((o) => o.slug === gewaehlt)) setGewaehlt(null)
  }, [data, gewaehlt, setGewaehlt])

  // Sichtbar bei mehreren Organisationen — und IMMER, solange eine Wahl
  // aktiv ist: sonst könnte eine gespeicherte Wahl nie zurückgenommen werden.
  if (!data || (data.length < 2 && !gewaehlt)) return null

  return (
    <label className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm text-slate-300 hover:bg-slate-800">
      <Building2 className="h-4 w-4" />
      <select
        value={gewaehlt ?? ""}
        onChange={(e) => {
          setGewaehlt(e.target.value || null)
          // Alles neu laden — jede Liste hängt an der Organisation
          void queryClient.invalidateQueries()
        }}
        aria-label="Organisation wählen"
        className="max-w-[12rem] bg-transparent text-sm text-white outline-none"
      >
        <option value="" className="text-slate-900">
          Alle Organisationen
        </option>
        {data.map((o) => (
          <option key={o.slug} value={o.slug} className="text-slate-900">
            {o.name}
          </option>
        ))}
      </select>
    </label>
  )
}
